import * as React from 'react';
import { injectIntl, WrappedComponentProps as InjectedIntlProps } from 'react-intl';
import { connect } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router';
import { compose, Dispatch } from 'redux';
import { Modal, Button } from 'semantic-ui-react';

import { cloneAlphaFromContent } from '../../../actions/EditorActions/EditorActions';
import { DispatchConnect } from '../../../types';
import { EditorCloseTabModal } from '../../Editor/EditorCloseTabModal/EditorCloseTabModal';
import { addFlashMessages } from '../../../actions/FlashMessagesActions';
import { FormattedMessageHelper } from '../../../shared/helpers/internationalization';
import { isLocalStorageAvailable, storeClonePayload, CloneAlphaPayload } from '../../../shared/helpers/clone-alpha';
import { PUBLIC_URL } from '../../../config';

import './Block.scss';
import analytics from '../../../shared/services/analytics/analytics';

interface CloneAlphaStateDispatchers {
  dispatchAddFlashMessages: (messages: any[], options?: any) => void;
  dispatchCloneAlphaFromContent: (alphaDetail: any) => void;
}

interface CloneAlphaProps {
  alpha: any;
  // This function will handle the clone alpha when the user is on the simulate page
  handleSimulateClone?: (alpha: string) => void | undefined;
  learnBlockId?: string;
  handleShowModal?: () => boolean;
  isMultiTab?: boolean;
  buttonLabelKey?: string;
  buttonClass?: string;
  containerClass?: string;
  isDisabled?: boolean;
}

interface CloneAlphaState {
  showChangeCodeModal: boolean;
}

type CloneAlphaGeneric = CloneAlphaStateDispatchers & CloneAlphaProps & RouteComponentProps<{}> & InjectedIntlProps;

class CloneAlpha extends React.Component<CloneAlphaGeneric, CloneAlphaState> {
  state = {
    showChangeCodeModal: false,
  };

  handleSimulateCloneModal = () => {
    if (this.props.handleSimulateClone) {
      this.props.handleSimulateClone(this.props.alpha);
    }
    if (this.state.showChangeCodeModal) {
      this.onCloseModal();
    }
  };

  onCloseModal = () => {
    this.setState({ showChangeCodeModal: false });
  };

  handleShowModal = () => {
    if (this.props.handleShowModal && this.props.handleSimulateClone) {
      const shouldOpenModal = this.props.handleShowModal();
      if (shouldOpenModal) {
        this.setState({ showChangeCodeModal: shouldOpenModal });
      } else {
        this.props.handleSimulateClone(this.props.alpha);
      }
    }
  };

  handleCloneAlphaFromContent = () => {
    if (!isLocalStorageAvailable()) {
      this.props.dispatchAddFlashMessages(
        [
          {
            msg: 'Unable to clone alpha: localStorage is not available. Opening in current tab instead.',
            defaultMessage: 'Unable to clone alpha: localStorage is not available. Opening in current tab instead.',
          },
        ],
        { type: 'warning' }
      );
      this.props.dispatchCloneAlphaFromContent(this.props.alpha);
      this.props.history.push('/simulate');
      return;
    }

    const alpha = this.props.alpha;

    // Handle both Immutable and plain JS objects
    const isImmutable = typeof alpha?.get === 'function';
    const get = (obj: any, key: string) => (isImmutable ? obj.get(key) : obj?.[key]);
    const getIn = (obj: any, path: string[]) => (isImmutable ? obj.getIn(path) : path.reduce((o: any, k: string) => o?.[k], obj));

    const regularCode = getIn(alpha, ['regular', 'code']) || get(alpha, 'regular') || get(alpha, 'code') || '';
    const settings = get(alpha, 'settings');
    const settingsObj = isImmutable && settings ? settings.toJS() : settings || {};

    const payload: CloneAlphaPayload = {
      code: regularCode,
      settings: settingsObj,
      visualization: settingsObj?.visualization,
      superAlpha: get(alpha, 'type') === 'SUPER',
      combo: get(alpha, 'type') === 'SUPER' ? getIn(alpha, ['combo', 'code']) : undefined,
      selection: get(alpha, 'type') === 'SUPER' ? getIn(alpha, ['selection', 'code']) : undefined,
      type: get(alpha, 'type'),
      color: get(alpha, 'color'),
      name: get(alpha, 'name'),
      tags: get(alpha, 'tags') ? (isImmutable ? get(alpha, 'tags').toJS() : get(alpha, 'tags')) : undefined,
      category: get(alpha, 'category'),
    };

    const nonce = storeClonePayload(payload);
    window.open(`${PUBLIC_URL}/simulate?cloneId=${nonce}`, '_blank');
  };

  /**
   * Determine if we are inside the Simulate page (e.g. /simulate/learn/...).
   * When inside the Simulate page, cloning should happen in the same tab.
   */
  private isInsideSimulatePage(): boolean {
    return this.props.location?.pathname?.startsWith('/simulate') ?? false;
  }

  handleClick = () => {
    if (this.props.learnBlockId) {
      analytics.trackCustomEvent('learn_click_simulate_alpha', { event_label: this.props.learnBlockId });
    }
    // If handleSimulateClone is provided, it means we are already in the Editor context
    // (e.g. Learn section in Simulate page). We should clone in the current tab.
    if (this.props.handleSimulateClone) {
      if (this.props.isMultiTab !== true) {
        this.handleShowModal();
      } else {
        // In multi-tab mode, we bypass the modal and clone directly
        this.props.handleSimulateClone(this.props.alpha);
      }
    } else if (this.isInsideSimulatePage()) {
      // We are on the simulate page but handleSimulateClone was not passed
      // (e.g. CloneAlpha rendered via BlockAlpha -> AlphaCode path).
      // Fall back to dispatching CLONE_ALPHA_FROM_CONTENT in the same tab.
      this.props.dispatchCloneAlphaFromContent(this.props.alpha);
    } else {
      // We are in a standalone context (e.g. Learn page, DataExplorer)
      // and should open the clone in a new browser tab.
      this.handleCloneAlphaFromContent();
    }
  };

  render() {
    const { buttonLabelKey = 'learn.cloneAlpha', buttonClass = 'alpha_code__clone-alpha-btn', containerClass = 'alpha_code' } = this.props;

    return (
      <div className={containerClass}>
        <Modal closeIcon={true} open={this.state.showChangeCodeModal} onClose={this.onCloseModal} size="tiny">
          <Modal.Content>
            <EditorCloseTabModal
              modalType="cloneSimulation"
              handleCancelModalCloseTab={this.onCloseModal}
              handleModalCloseTab={this.handleSimulateCloneModal}
            />
          </Modal.Content>
        </Modal>
        <Button onClick={this.handleClick} className={buttonClass} disabled={this.props.isDisabled}>
          <FormattedMessageHelper tagName="span" id={buttonLabelKey} />
        </Button>
      </div>
    );
  }
}

const mapDispatchToProps = (dispatch: Dispatch<any>): CloneAlphaStateDispatchers => ({
  dispatchAddFlashMessages: (messages: any[], options?: any) => {
    dispatch(addFlashMessages(messages, options));
  },
  dispatchCloneAlphaFromContent: (alphaDetail: any) => {
    dispatch(cloneAlphaFromContent(alphaDetail));
  },
});

export default compose<any>(
  injectIntl,
  withRouter,
  connect<CloneAlphaProps, DispatchConnect<CloneAlphaStateDispatchers>>(null, mapDispatchToProps)
)(CloneAlpha);
